import * as ipaddr from 'ipaddr.js';
import { z } from 'zod';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isStrictIPorCIDR(input: string): boolean {
  try {
    if (input.includes('/')) {
      const [ipPart, rangePart] = input.split('/');

      if (!ipPart || !rangePart) {
        return false;
      }

      if (!/^\d+$/.test(rangePart)) {
        return false;
      }

      const range = parseInt(rangePart, 10);
      const addr = ipaddr.parse(ipPart);

      if (addr.kind() === 'ipv4') {
        const validIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ipPart);
        return validIPv4 && range >= 0 && range <= 32;
      }

      // IPv6
      return range >= 0 && range <= 128;
    }

    if (!ipaddr.isValid(input)) {
      return false;
    }

    const addr = ipaddr.parse(input);

    if (addr.kind() === 'ipv4') {
      return /^\d{1,3}(\.\d{1,3}){3}$/.test(input);
    }

    return true;
  } catch {
    return false;
  }
}

const baseSchemaShape = {
  loginId: z.string().min(3, 'Login ID must be at least 3 characters'),
  distributionList: z.string().optional(),
  ipWhitelist: z.string().optional(),
  landingFolder: z.string().optional(),
  sfAccountId: z.string().optional(),
  contentAccessRole: z.string().min(1, 'Content access role is required'),
  orgName: z.string().optional(),
  rootFolder: z.string().min(1, 'Root folder is required'),
  serviceAccountOwner: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isSystemUser: z.boolean().default(false),
  isLoginAdmin: z.boolean().default(false),
  isEnabled: z.boolean().default(false),
} as const;

export type UserFormValues = {
  loginId: string;
  distributionList?: string;
  ipWhitelist?: string;
  landingFolder?: string;
  sfAccountId?: string;
  contentAccessRole: string;
  orgName?: string;
  rootFolder: string;
  serviceAccountOwner?: string;
  firstName?: string;
  lastName?: string;
  isSystemUser: boolean;
  isLoginAdmin: boolean;
  assignments?: string[];
  isEnabled: boolean;
};

interface SchemaOptions {
  restrictedNames: Set<string>;
  restrictedFolders: Set<string>;
  requireAssignments: boolean;
  currentUserRoles?: Array<{ role: string }>;
}

function validateLandingFolder(
  restrictedNames: Set<string>,
  restrictedFolders: Set<string>,
  currentUserRoles?: Array<{ role: string }>,
) {
  // Check if user is Admin or Operator - they can bypass restrictions
  const isAdminOrOperator = currentUserRoles?.some(
    r => r.role === 'ADMIN' || r.role === 'OPERATOR',
  );

  const baseValidation = z
    .string()
    .refine(value => (value === '' ? true : value.startsWith('/')), {
      message: 'Landing folder should start with a forward slash (/)',
    })
    .refine(value => (value === '' ? true : value.length > 1 ? !value.endsWith('/') : true), {
      message: 'Landing folder should not end with a forward slash (/)',
    })
    .refine(item => !item.includes('\\'), {
      message: 'Landing folder can not contain back slash "(\\)".',
    });

  // Only apply folder name restrictions for non-Admin/Operator users
  if (isAdminOrOperator) {
    return baseValidation.optional();
  }

  return baseValidation
    .refine(value => !restrictedNames.has(value) && !restrictedFolders.has(value), {
      message: 'This folder name is not allowed as Landing folder.',
    })
    .optional();
}

function validateSystemUserFields(data: UserFormValues, ctx: z.RefinementCtx) {
  if (!data.serviceAccountOwner?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['serviceAccountOwner'],
      message: 'Service account owner is required for system users',
    });
    return;
  }

  if (!EMAIL_REGEX.test(data.serviceAccountOwner)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['serviceAccountOwner'],
      message: 'Service account owner must be a valid email address',
    });
  }
}

function validateRegularUserFields(data: UserFormValues, ctx: z.RefinementCtx) {
  if (!data.firstName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['firstName'],
      message: 'First name is required for non-system users',
    });
  }

  if (!data.lastName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['lastName'],
      message: 'Last name is required for non-system users',
    });
  }

  if (!EMAIL_REGEX.test(data.loginId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['loginId'],
      message: 'Login ID must be a valid email for non-system users',
    });
  }
}

function validateDistributionList(data: UserFormValues, ctx: z.RefinementCtx) {
  if (!data.distributionList?.trim()) {
    return;
  }

  const emails = data.distributionList
    .split(/[\n,]/)
    .map(s => s.trim())
    .filter(Boolean);

  const invalidEmails = emails.filter(email => !EMAIL_REGEX.test(email));

  if (invalidEmails.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['distributionList'],
      message: `Invalid email addresses: ${invalidEmails.join(', ')}`,
    });
  }
}

function validateIpWhitelist(data: UserFormValues, ctx: z.RefinementCtx) {
  if (!data.ipWhitelist?.trim()) {
    return;
  }

  const ipRanges = data.ipWhitelist
    .split(/[\n,]/)
    .map(s => s.trim())
    .filter(Boolean);

  const invalidIPs = ipRanges.filter(ip => !isStrictIPorCIDR(ip));

  if (invalidIPs.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ipWhitelist'],
      message: `Invalid IP addresses or CIDR notation: ${invalidIPs.join(', ')}`,
    });
  }
}

export function createUserFormSchema({
  restrictedNames,
  restrictedFolders,
  requireAssignments,
  currentUserRoles,
}: SchemaOptions) {
  const assignmentsSchema = requireAssignments
    ? z.array(z.string()).min(1, 'At least one assignment is required')
    : z.array(z.string()).optional().default([]);

  const baseSchema = z.object({
    ...baseSchemaShape,
    assignments: assignmentsSchema,
  });

  return baseSchema
    .extend({
      landingFolder: validateLandingFolder(restrictedNames, restrictedFolders, currentUserRoles),
    })
    .superRefine((data, ctx) => {
      if (data.isSystemUser) {
        validateSystemUserFields(data, ctx);
      } else {
        validateRegularUserFields(data, ctx);
      }

      validateDistributionList(data, ctx);
      validateIpWhitelist(data, ctx);
    });
}
